/**
 * Sesión del jugador.
 *
 * Tres formas de entrar, por orden de fricción:
 *
 * - **Anónima.** Un toque y a jugar. Es la que importa en móvil: pedir un correo
 *   antes de dejar ver el juego es donde se pierde a la mayoría de la gente. La
 *   cuenta es real (existe en auth.users, tiene perfil, colección y mazo), solo
 *   que sin credenciales para recuperarla.
 * - **Correo y contraseña.** Para conservar la colección y jugar desde otro
 *   dispositivo.
 * - **Vincular la cuenta anónima.** Convierte la anónima en permanente sin
 *   perder nada, porque es el mismo `auth.users.id`: la colección, el oro y los
 *   mazos siguen colgando de él. Es la conversión que hay que ofrecer justo
 *   después de la primera victoria, no antes de la primera partida.
 */

import { useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { isSupabaseConfigured, supabase } from '@/services/supabaseClient';

export interface AuthState {
  session: Session | null;
  user: User | null;
  /** Todavía no se sabe si hay sesión guardada: no decidas nada aún. */
  isLoading: boolean;
  /** La sesión es anónima: conviene invitar a vincularla en algún momento. */
  isAnonymous: boolean;
  /** No hay backend configurado; se juega en local. */
  isOffline: boolean;
  error: string | null;
}

export interface AuthActions {
  signInAnonymously: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, username?: string) => Promise<void>;
  linkEmailToAnonymous: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export function useAuth(): AuthState & AuthActions {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(isSupabaseConfigured);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    let active = true;

    // Al arrancar puede haber una sesión guardada en localStorage. Hasta que se
    // resuelve esta promesa no se sabe, y pintar la pantalla de inicio de sesión
    // mientras tanto produce un parpadeo en cada recarga.
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setIsLoading(false);
    });

    // Cubre el refresco del token, el cierre de sesión desde otra pestaña y la
    // vuelta de un enlace mágico.
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!active) return;
      setSession(nextSession);
      setIsLoading(false);
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  /** Envuelve una operación de auth: limpia el error previo y traduce el fallo. */
  const run = async (operation: () => Promise<{ error: { message: string } | null }>) => {
    setError(null);
    const client = supabase;
    if (!client) {
      setError('No hay backend configurado.');
      return;
    }
    const { error: opError } = await operation();
    if (opError) setError(translateAuthError(opError.message));
  };

  return {
    session,
    user: session?.user ?? null,
    isLoading,
    isAnonymous: session?.user?.is_anonymous === true,
    isOffline: !isSupabaseConfigured,
    error,

    signInAnonymously: () => run(() => supabase!.auth.signInAnonymously()),

    signInWithEmail: (email, password) =>
      run(() => supabase!.auth.signInWithPassword({ email, password })),

    signUpWithEmail: (email, password, username) =>
      run(() =>
        supabase!.auth.signUp({
          email,
          password,
          // El trigger handle_new_user lee este metadato para el nombre. Si no
          // llega, o si no es válido, la base fabrica uno; el registro nunca
          // falla por culpa del nombre.
          options: { data: username ? { username } : undefined },
        }),
      ),

    // updateUser sobre una sesión anónima le añade credenciales conservando el
    // mismo id de usuario. Por eso no hay nada que migrar: las cartas, el oro y
    // los mazos ya apuntan a ese id.
    linkEmailToAnonymous: (email, password) =>
      run(() => supabase!.auth.updateUser({ email, password })),

    signOut: () => run(() => supabase!.auth.signOut()),
  };
}

/** Mensajes de GoTrue en castellano y en términos de lo que hay que hacer. */
function translateAuthError(message: string): string {
  const normalized = message.toLowerCase();
  if (normalized.includes('invalid login credentials')) {
    return 'Correo o contraseña incorrectos.';
  }
  if (normalized.includes('user already registered') || normalized.includes('already been registered')) {
    return 'Ese correo ya tiene cuenta. Inicia sesión.';
  }
  if (normalized.includes('password should be at least')) {
    return 'La contraseña necesita al menos 6 caracteres.';
  }
  if (normalized.includes('email not confirmed')) {
    return 'Confirma el correo antes de entrar.';
  }
  if (normalized.includes('anonymous sign-ins are disabled')) {
    return 'El acceso anónimo está desactivado en el proyecto de Supabase.';
  }
  if (normalized.includes('rate limit') || normalized.includes('too many')) {
    return 'Demasiados intentos. Espera un momento.';
  }
  return message;
}
